/**
 * Cart Count Web Component
 * Self-updating cart count badge that responds to cart changes
 */
class CartCountComponent extends HTMLElement {
  constructor() {
    super();
    this.updateCount = this.updateCount.bind(this);
    this.previousCount = null; // null means not initialized yet
    this.isInitialLoad = true;
  }
  
  connectedCallback() {
    this.render();
    
    // Listen for cart events from theme
    document.documentElement.addEventListener('cart:rendered', this.updateCount);
    document.documentElement.addEventListener('cart:refresh', this.updateCount);
    
    // Listen for product added events
    document.addEventListener('product:added', this.updateCount);
    document.addEventListener('cart:updated', this.updateCount);
    
    // Custom event for manual updates
    document.addEventListener('cart:count-changed', this.updateCount);
    
    // Initial fetch
    this.fetchCartCount();
  }
  
  disconnectedCallback() {
    document.documentElement.removeEventListener('cart:rendered', this.updateCount);
    document.documentElement.removeEventListener('cart:refresh', this.updateCount);
    document.removeEventListener('product:added', this.updateCount);
    document.removeEventListener('cart:updated', this.updateCount);
    document.removeEventListener('cart:count-changed', this.updateCount);
  }
  
  async fetchCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.count = cart.item_count;
      
      // Set previousCount on initial load without triggering drawer
      if (this.isInitialLoad) {
        this.previousCount = this.count;
        this.isInitialLoad = false;
      }
      
      this.render();
    } catch (error) {
      console.error('[CartCount] Error fetching cart:', error);
    }
  }
  
  updateCount(event) {
    // Store previous count before updating
    const previousCount = this.count || 0;
    
    // Mark that this is not initial load (it's an event-driven update)
    this.isInitialLoad = false;
    
    // If event has detail with count, use it
    if (event && event.detail && typeof event.detail.count !== 'undefined') {
      this.count = event.detail.count;
      this.render(previousCount);
    } else if (event && event.detail && typeof event.detail.item_count !== 'undefined') {
      this.count = event.detail.item_count;
      this.render(previousCount);
    } else {
      this.fetchCartCountAndRender(previousCount);
    }
  }
  
  async fetchCartCountAndRender(previousCount) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.count = cart.item_count;
      this.render(previousCount);
    } catch (error) {
      console.error('[CartCount] Error fetching cart:', error);
    }
  }
  
  render(previousCount = null) {
    const count = this.count || 0;
    
    if (count > 0) {
      this.textContent = count;
      this.style.display = 'inline-flex';
      
      // Add pulse animation
      this.classList.add('cart-count-updated');
      setTimeout(() => {
        this.classList.remove('cart-count-updated');
      }, 300);
      
      // Only open drawer if:
      // 1. Not initial load
      // 2. Count increased (product was added)
      // 3. previousCount is not null (we have a valid previous state)
      if (!this.isInitialLoad && previousCount !== null && count > previousCount) {
        this.openCartDrawer();
      } 
    } else {
      this.textContent = '';
      this.style.display = 'none';
    }
  }
  
  openCartDrawer() {
    // Find the cart drawer element
    const cartDrawer = document.getElementById('sidebar-cart');
    
    if (!cartDrawer) {
      console.warn('[CartCount] Cart drawer not found');
      return;
    }
    
    // Check if drawer is already open
    if (cartDrawer.getAttribute('aria-hidden') === 'false') {
      return;
    }
    
    // Find the open drawer button/link
    const openDrawerButton = document.querySelector('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]');
    
    if (openDrawerButton) {
      openDrawerButton.click();
    } else {
      this.manuallyOpenDrawer(cartDrawer);
    }
  }
  
  manuallyOpenDrawer(drawer) {
    // Set aria-hidden to false
    drawer.setAttribute('aria-hidden', 'false');
    
    // Add no-scroll class to prevent body scrolling
    document.documentElement.classList.add('no-scroll');
    
    // Show page overlay
    const pageOverlay = document.querySelector('.PageOverlay');
    if (pageOverlay) {
      pageOverlay.classList.add('is-visible');
    }
    
    // Calculate max height for drawer
    drawer.style.maxHeight = window.innerHeight + 'px';
    
  }
}

// Register the custom element
if (!customElements.get('cart-count')) {
  customElements.define('cart-count', CartCountComponent);
}
